import React from 'react'

import DataHealthKpiCard from 'components_new/organisms/internal/DataHealth/KpiCard'
import DataHealthKpiGrid from 'components_new/organisms/internal/DataHealth/KpiGrid'

import { Aggregates } from 'redux/selectors/internal/DataRuns'
import { formatCostUsd, formatFailRate } from '../utils'

export interface DataRunsKpiRowProps {
  aggregates: Aggregates
  /** null in the all-customers scope. */
  customerName: string | null
  okJobCount: number
  orderWarningCount: number
  totalCustomers: number
}

/**
 * Four KPI cards - same metrics in both page states, only the scope (and a
 * couple of sub-labels) changes. Colors are palette references only (see
 * CLAUDE.md): no hex literals.
 *
 * Only the failure card is filled, and only green when there is nothing
 * wrong. The design handoff also fills the job-count card green, but that
 * count is a neutral fact: a green 32 next to a red 4 reads as "all fine"
 * when four of those jobs failed. Green carries one meaning on this row.
 */
const DataRunsKpiRow = (props: DataRunsKpiRowProps) => {
  const {
    aggregates,
    customerName,
    okJobCount,
    orderWarningCount,
    totalCustomers
  } = props

  const hasFailures = aggregates.fail_count > 0
  const failRate = formatFailRate(aggregates.fail_count, aggregates.job_count)

  /*
   * The failure share lives here rather than in a card of its own: as a
   * rate over the latest run it is the same fact as this card's count,
   * just easier to compare between customers and nights.
   */
  let failSub: string

  if (!hasFailures) {
    failSub = 'allt gick igenom'
  } else if (customerName) {
    failSub = failRate
      ? `av ${aggregates.job_count} jobb · ${failRate}`
      : `av ${aggregates.job_count} jobb`
  } else {
    failSub = failRate
      ? `${aggregates.affected_customers} kunder · ${failRate} av jobben`
      : `${aggregates.affected_customers} kunder påverkade`
  }

  return (
    <DataHealthKpiGrid>
      <DataHealthKpiCard
        fill={
          hasFailures
            ? {
                bg: 'error.transparent',
                border: 'error.light',
                text: 'error.text'
              }
            : {
                bg: 'brand.background',
                border: 'brand.backgroundDark',
                text: 'brand.text'
              }
        }
        label="Fel i senaste körningen"
        sub={failSub}
        value={aggregates.fail_count}
      />
      <DataHealthKpiCard
        label="Fel ordning"
        sub="startade före sitt beroende"
        value={orderWarningCount}
      />
      <DataHealthKpiCard
        label="Est. kostnad"
        sub="för senaste körningen"
        value={formatCostUsd(aggregates.estimated_cost_usd)}
      />
      <DataHealthKpiCard
        label="Jobb i natt"
        sub={
          customerName
            ? `${okJobCount} av ${aggregates.job_count} lyckades`
            : `${totalCustomers} kunder`
        }
        value={aggregates.job_count}
      />
    </DataHealthKpiGrid>
  )
}

DataRunsKpiRow.displayName = 'DataRunsKpiRow'
export default DataRunsKpiRow
